import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent } from "@material-ui/core";

import mammoth from "mammoth";
import icons from "../../../../../assets/icons/icon-collection.svg";
import { SectionLoader } from "../../../../../components/PageLoader";

const DocumentViewer = ({ open, onClose, documentUrl, documentName }) => {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);
  const [fileType, setFileType] = useState(null);

  useEffect(() => {
    if (open && documentUrl) {
      loadDocument();
    }
    return () => {
      setContent(null);
      setError(null);
    };
  }, [open, documentUrl]);

  const getFileExtension = (url) => {
    const extension = url.split(".").pop().toLowerCase().split("?")[0];
    return extension;
  };

  const loadDocument = async () => {
    setLoading(true);
    setError(null);

    try {
      const extension = getFileExtension(documentUrl);
      setFileType(extension);

      if (extension === "pdf") {
        // For PDF, we'll use an iframe or embed
        setContent({ type: "pdf", url: documentUrl });
      } else if (["doc", "docx"].includes(extension)) {
        await loadDOCX(documentUrl);
      } else {
        setError("Unsupported file format. Please download to view.");
      }
    } catch (err) {
      console.error("Error loading document:", err);
      setError("Failed to load document. Please try downloading instead.");
    } finally {
      setLoading(false);
    }
  };

  const loadDOCX = async (url) => {
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();

      const result = await mammoth.convertToHtml({ arrayBuffer });
      setContent({ type: "docx", html: result.value });
    } catch (err) {
      throw new Error("Failed to load DOCX: " + err.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        disableTypography
        style={{
          position: "relative",
          padding: "1rem 1rem 1rem 1.5rem",
          minHeight: "3.5rem",
          fontSize: "1.1rem",
          fontWeight: 600,
        }}
      >
        <span>{documentName || "Document Viewer"}</span>
        <button type="button" className="document-viewer__close-btn" onClick={onClose} aria-label="Close document viewer">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#4a4a4a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <SectionLoader />
          </div>
        )}

        {error && (
          <div
            className="alert alert-warning text-center"
            style={{ margin: "2rem" }}
          >
            <p>{error}</p>
            <a href={documentUrl} download className="btn btn-primary mt-2">
              Download Document
            </a>
          </div>
        )}

        {!loading && !error && content && (
          <div>
            {content.type === "pdf" && (
              <div className="pdf-viewer" style={{ height: "70vh" }}>
                <iframe
                  src={content.url}
                  title="PDF Viewer"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              </div>
            )}

            {content.type === "docx" && (
              <div
                className="docx-viewer p-4"
                style={{
                  backgroundColor: "#fff",
                  minHeight: "400px",
                }}
                dangerouslySetInnerHTML={{ __html: content.html }}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewer;